<script setup>
// NOTICE: KAPTION-INTEGRATION
// Single column in the Sales Funnel Kanban board
// Renders a colored header + scrollable list of KaptionSalesKanbanCard

import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import KaptionSalesKanbanCard from './KaptionSalesKanbanCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Number,
    default: 0,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  isDarkMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cardClick', 'loadMore', 'dragAdd']);

const { t } = useI18n();
const scrollContainer = ref(null);
const showTopShadow = ref(false);
const showBottomShadow = ref(false);

const cardCount = computed(() => props.total || props.conversations.length);

function handleScroll(e) {
  const el = e.target;
  showTopShadow.value = el.scrollTop > 0;
  showBottomShadow.value =
    el.scrollHeight - (el.scrollTop + el.clientHeight) > 10;

  // Infinite scroll: load more when near bottom
  if (
    props.hasMore &&
    !props.loading &&
    el.scrollHeight - (el.scrollTop + el.clientHeight) < 100
  ) {
    emit('loadMore', props.stage.key);
  }
}

function onDragChange(evt) {
  // vuedraggable fires 'added' when a card is dropped INTO this column
  if (evt.added) {
    emit('dragAdd', {
      conversation: evt.added.element,
      toStage: props.stage.key,
    });
  }
}

// Check scroll shadows on next tick after data changes
function updateShadows() {
  nextTick(() => {
    const el = scrollContainer.value;
    if (!el) return;
    showTopShadow.value = el.scrollTop > 0;
    showBottomShadow.value =
      el.scrollHeight - (el.scrollTop + el.clientHeight) > 10;
  });
}

defineExpose({ updateShadows });
</script>

<template>
  <div
    class="kanban-column flex flex-col"
    :class="isDarkMode ? 'kanban-column-dark' : 'kanban-column-light'"
  >
    <!-- Column header with colored top border -->
    <div
      class="kanban-column-header"
      :style="{ borderTopColor: stage.colorHex }"
    >
      <div class="flex items-center gap-1.5">
        <i
          class="size-4"
          :class="stage.icon"
          :style="{ color: stage.colorHex }"
        />
        <span
          class="text-xs font-semibold"
          :class="
            isDarkMode
              ? 'text-kpt-text-primary-dark'
              : 'text-kpt-text-primary-light'
          "
        >
          {{ stage.label }}
        </span>
      </div>
      <span
        class="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
        :class="
          isDarkMode
            ? 'bg-kpt-dark-600 text-kpt-text-secondary-dark'
            : 'bg-kpt-light-300 text-kpt-text-secondary-light'
        "
      >
        {{ cardCount }}
      </span>
    </div>

    <!-- Scroll shadow indicators -->
    <div class="relative flex-1 min-h-0 overflow-hidden">
      <div
        v-if="showTopShadow"
        class="absolute top-0 left-0 right-0 h-3 z-10 pointer-events-none"
        :class="
          isDarkMode
            ? 'bg-gradient-to-b from-kpt-dark-800/80 to-transparent'
            : 'bg-gradient-to-b from-kpt-light-100/80 to-transparent'
        "
      />

      <!-- Scrollable card area with draggable -->
      <div
        ref="scrollContainer"
        class="kanban-column-body"
        @scroll="handleScroll"
      >
        <draggable
          :list="conversations"
          group="kanban"
          item-key="id"
          :animation="200"
          ghost-class="sortable-ghost"
          drag-class="sortable-drag"
          class="min-h-full"
          @change="onDragChange"
        >
          <template #item="{ element }">
            <KaptionSalesKanbanCard
              :conversation="element"
              :stage-color="stage.colorHex"
              :is-dark-mode="isDarkMode"
              @click="emit('cardClick', element)"
            />
          </template>
        </draggable>

        <!-- Loading spinner -->
        <div v-if="loading" class="flex justify-center py-3">
          <Spinner class="text-n-brand" />
        </div>

        <!-- Empty state -->
        <div
          v-if="!loading && !conversations.length"
          class="flex flex-col items-center justify-center py-8 px-3"
        >
          <i
            class="size-8 mb-2 opacity-40"
            :class="stage.icon"
            :style="{ color: stage.colorHex }"
          />
          <span
            class="text-xs text-center"
            :class="
              isDarkMode
                ? 'text-kpt-text-muted-dark'
                : 'text-kpt-text-muted-light'
            "
          >
            {{ t('KAPTION_PLATFORM.SALES_KANBAN.NO_CONVERSATIONS') }}
          </span>
        </div>
      </div>

      <div
        v-if="showBottomShadow"
        class="absolute bottom-0 left-0 right-0 h-3 z-10 pointer-events-none"
        :class="
          isDarkMode
            ? 'bg-gradient-to-t from-kpt-dark-800/80 to-transparent'
            : 'bg-gradient-to-t from-kpt-light-100/80 to-transparent'
        "
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.kanban-column {
  flex: 1 1 0;
  min-width: 200px;
  border-radius: 0.5rem;
  overflow: hidden;
}

.kanban-column-dark {
  background: rgba(23, 23, 32, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.kanban-column-light {
  background: rgba(248, 248, 250, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  border-top: 3px solid;
}

.kanban-column-body {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  padding: 0.25rem 0;

  // Custom thin scrollbar
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(128, 128, 128, 0.3);
    border-radius: 2px;
  }
}

// Drag over highlight
.kanban-column:has(.sortable-ghost) {
  .kanban-column-header {
    opacity: 0.9;
  }
}
</style>
